/**
 * Worker Health — probes deployed workers directly from the browser.
 *
 * The ops-api returns worker definitions (name, health URL, deployed flag).
 * Only workers marked `deployed: true` are probed. Undeployed workers are
 * rendered as "Not Deployed" without generating any network requests.
 */
import React, { useCallback, useState, useEffect, useRef } from "react";
import { workers as workersApi } from "../api.js";
import { useAsync } from "../hooks.js";

type WorkerStatus = "online" | "degraded" | "offline" | "not_deployed" | "probing";

interface WorkerDef {
  name: string;
  displayName: string;
  healthUrl: string;
  deployed: boolean;
}

interface ProbeResult {
  name: string;
  displayName: string;
  healthUrl: string;
  deployed: boolean;
  status: WorkerStatus;
  latencyMs: number | null;
  lastSuccessAt: string | null;
  httpStatus: number | null;
  error: string | null;
}

function statusBadge(status: WorkerStatus) {
  if (status === "online")       return <span className="ops-badge ops-badge-green">Online</span>;
  if (status === "degraded")     return <span className="ops-badge ops-badge-amber">Degraded</span>;
  if (status === "offline")      return <span className="ops-badge ops-badge-red">Offline</span>;
  if (status === "not_deployed") return <span className="ops-badge ops-badge-neutral">Not Deployed</span>;
  return <span className="ops-badge ops-badge-neutral">Probing…</span>;
}

function latencyColor(ms: number | null): string {
  if (ms == null) return "var(--ops-muted)";
  if (ms < 200)   return "var(--ops-success)";
  if (ms < 800)   return "var(--ops-warning)";
  return "var(--ops-danger)";
}

async function probeWorker(worker: WorkerDef): Promise<ProbeResult> {
  // Skip probing for undeployed workers
  if (!worker.deployed) {
    return { ...worker, status: "not_deployed", latencyMs: null, lastSuccessAt: null, httpStatus: null, error: null };
  }

  const start = Date.now();
  try {
    const controller = new AbortController();
    const tid = setTimeout(() => controller.abort(), 5000);
    const resp = await fetch(worker.healthUrl, {
      method: "GET",
      signal: controller.signal,
      headers: { Accept: "application/json" },
      mode: "cors",
    });
    clearTimeout(tid);
    const latencyMs = Date.now() - start;
    const contentType = resp.headers.get("Content-Type") ?? "";

    if (resp.ok && contentType.includes("application/json")) {
      return { ...worker, status: "online", latencyMs, lastSuccessAt: new Date().toISOString(), httpStatus: resp.status, error: null };
    }

    return {
      ...worker,
      status: resp.ok ? "degraded" : (resp.status >= 500 ? "degraded" : "offline"),
      latencyMs, lastSuccessAt: null, httpStatus: resp.status,
      error: resp.ok ? "Non-JSON response" : `HTTP ${resp.status}`,
    };
  } catch (err: any) {
    const msg = err?.message ?? "Network error";
    return {
      ...worker, status: "offline", latencyMs: Date.now() - start,
      lastSuccessAt: null, httpStatus: null,
      error: msg.includes("Failed to fetch") || msg.includes("NetworkError") ? "Unreachable" : msg,
    };
  }
}

export function WorkersView() {
  const defFetcher = useCallback(() => workersApi.all(), []);
  const { data: defData, loading: defLoading, error: defError } = useAsync<any>(defFetcher, []);

  const [probes, setProbes] = useState<ProbeResult[]>([]);
  const [probing, setProbing] = useState(false);
  const [lastProbedAt, setLastProbedAt] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const definitions: WorkerDef[] = defData?.workers ?? [];
  const hasDeployed = definitions.some((d) => d.deployed);

  const runProbes = useCallback(async () => {
    if (definitions.length === 0) return;
    setProbing(true);
    const results = await Promise.all(definitions.map(probeWorker));
    setProbes(results);
    setLastProbedAt(new Date().toLocaleTimeString());
    setProbing(false);
  }, [definitions]);

  // Probe immediately when definitions arrive
  useEffect(() => {
    if (definitions.length > 0) runProbes();
  }, [definitions.length]);

  // Auto-refresh every 15s — only if there are deployed workers to probe
  useEffect(() => {
    if (!hasDeployed) return;
    intervalRef.current = setInterval(runProbes, 15_000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [runProbes, hasDeployed]);

  const displayList: ProbeResult[] = probes.length > 0
    ? probes
    : definitions.map((d) => ({
        ...d,
        status: d.deployed ? ("probing" as WorkerStatus) : ("not_deployed" as WorkerStatus),
        latencyMs: null, lastSuccessAt: null, httpStatus: null, error: null,
      }));

  return (
    <div className="ops-page">
      <div className="ops-page-header">
        <div>
          <h1 className="ops-page-title">Worker Health</h1>
          {lastProbedAt && (
            <p className="ops-page-subtitle">
              Last probed at {lastProbedAt} · auto-refreshes every 15 s
            </p>
          )}
        </div>
        <button
          className="ops-btn ops-btn-secondary ops-btn-sm"
          onClick={runProbes}
          disabled={probing || !hasDeployed}
        >
          {probing ? "Probing…" : "↻ Probe now"}
        </button>
      </div>

      {defError && <div className="ops-error">{defError}</div>}
      {defLoading && definitions.length === 0 && <div className="ops-loading">Loading worker definitions…</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        {displayList.map((w) => (
          <div
            key={w.name}
            className="ops-card"
            style={{
              display: "flex", alignItems: "center", gap: "1rem",
              opacity: w.status === "not_deployed" ? 0.55 : 1,
            }}
          >
            <div style={{ minWidth: 180 }}>
              <div style={{ fontWeight: 700, fontSize: "0.9375rem" }}>{w.displayName}</div>
              <div style={{ fontSize: "0.75rem", color: "var(--ops-muted)" }}>{w.name}</div>
            </div>

            <div style={{ minWidth: 110 }}>{statusBadge(w.status)}</div>

            {w.status !== "not_deployed" && (
              <>
                <div style={{ minWidth: 100 }}>
                  {w.latencyMs != null ? (
                    <span style={{ color: latencyColor(w.latencyMs), fontWeight: 600, fontSize: "0.875rem" }}>
                      {w.latencyMs} ms
                    </span>
                  ) : (
                    <span style={{ color: "var(--ops-muted)", fontSize: "0.8125rem" }}>—</span>
                  )}
                  <div style={{ fontSize: "0.7rem", color: "var(--ops-muted)" }}>latency</div>
                </div>

                <div style={{ minWidth: 120 }}>
                  {w.lastSuccessAt ? (
                    <>
                      <div style={{ fontSize: "0.8125rem" }}>{new Date(w.lastSuccessAt).toLocaleTimeString()}</div>
                      <div style={{ fontSize: "0.7rem", color: "var(--ops-muted)" }}>last success</div>
                    </>
                  ) : (
                    <span style={{ color: w.status === "probing" ? "var(--ops-muted)" : "var(--ops-danger)", fontSize: "0.8125rem" }}>
                      {w.status === "probing" ? "—" : "Never"}
                    </span>
                  )}
                </div>

                {w.error && (
                  <div><span className="ops-badge ops-badge-red">{w.error}</span></div>
                )}
              </>
            )}

            {w.status === "not_deployed" && (
              <div style={{ fontSize: "0.8125rem", color: "var(--ops-muted)", fontStyle: "italic" }}>
                Coming soon — not yet deployed to production
              </div>
            )}

            {w.deployed && w.healthUrl && (
              <div style={{ marginLeft: "auto" }}>
                <a href={w.healthUrl} target="_blank" rel="noreferrer"
                  style={{ fontSize: "0.75rem", color: "var(--ops-muted)", textDecoration: "none" }}>
                  {w.healthUrl} ↗
                </a>
              </div>
            )}
          </div>
        ))}
      </div>

      {!defLoading && definitions.length === 0 && (
        <div className="ops-empty">No worker definitions configured.</div>
      )}
    </div>
  );
}
